// Shared inverted-hull outline state/helper for StaticOutlineRenderPipeline
// and SkinnedOutlineRenderPipeline. Both concrete pipelines need identical
// width/color uniform handling and identical fixed-function pipeline state
// (front-face culling, depth test/write behavior) - factoring that into one
// small helper avoids duplicating it a third and fourth time on top of the
// existing StaticRenderPipeline/SkinnedRenderPipeline/
// PapercraftStaticRenderPipeline triplication (see architecture notes),
// without disturbing the empty `RenderPipeline` base class or either
// existing concrete pipeline.
//
// Opt-in only: nothing in this file is wired into Character or any other
// scene object by default - a scene object must explicitly construct a
// Static/SkinnedOutlineRenderPipeline and call prepare()/draw() itself.

// Default outline width, in world units (same units as Transform/mesh
// geometry - there is no screen-space pass anywhere in this engine to
// convert a pixel width into a resolution-independent NDC offset, so
// world-space is the only consistent choice here).
export const DEFAULT_OUTLINE_WIDTH = 0.02;

// Default outline color: opaque black (flat silhouette color).
export const DEFAULT_OUTLINE_COLOR: readonly [number, number, number, number] = [0, 0, 0, 1];

// OutlineUniform WGSL layout (see outline_common.wgsl):
//   offset 0  : width (f32)
//   offset 4  : pad0  (f32)
//   offset 8  : pad1  (f32)
//   offset 12 : pad2  (f32)
//   offset 16 : color (vec4<f32>)
// Total 32 bytes - already a multiple of 4 (and of 16), so no special
// alignment handling is needed beyond using a fixed-size Float32Array,
// matching this codebase's existing pattern for uniform buffers (camera/
// object/paper buffers in static_renderpipeline.ts / skinned_renderpipeline.ts
// are all plain fixed-size Float32Array writes with no dynamic sizing).
export const OUTLINE_UNIFORM_BYTES = 32;
const OUTLINE_UNIFORM_FLOATS = OUTLINE_UNIFORM_BYTES / 4;

/**
 * Holds the outline width/color state and the small GPU uniform buffer
 * that both outline pipelines write it into every prepare(). Not a
 * RenderPipeline itself (it has no vertex/fragment stage of its own) -
 * StaticOutlineRenderPipeline and SkinnedOutlineRenderPipeline each own
 * one instance and delegate width/color storage + buffer upload to it.
 */
export class OutlineState {
  private width: number;
  private color: [number, number, number, number];
  private scratch: Float32Array<ArrayBuffer>;

  outline_buffer: GPUBuffer | null = null;

  constructor(
    width: number = DEFAULT_OUTLINE_WIDTH,
    color: readonly [number, number, number, number] = DEFAULT_OUTLINE_COLOR
  ) {
    this.width = width;
    this.color = [color[0], color[1], color[2], color[3]];
    this.scratch = new Float32Array(OUTLINE_UNIFORM_FLOATS);
  }

  /** Sets the outline extrusion width, in world units. */
  set_line_width(width: number): void {
    this.width = width;
  }

  get_line_width(): number {
    return this.width;
  }

  /** Sets the flat outline color. Defaults to opaque black. */
  set_line_color(r: number, g: number, b: number, a: number): void {
    this.color[0] = r;
    this.color[1] = g;
    this.color[2] = b;
    this.color[3] = a;
  }

  get_line_color(): readonly [number, number, number, number] {
    return this.color;
  }

  /** Allocates the outline uniform buffer. Call once during build(). */
  build(device: GPUDevice, label: string): void {
    this.outline_buffer = device.createBuffer({
      label: label + "_outline_uniform",
      size: OUTLINE_UNIFORM_BYTES,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });
  }

  /** Writes the current width/color into the uniform buffer. Call every prepare(). */
  write(device: GPUDevice): void {
    if (!this.outline_buffer) return;

    this.scratch[0] = this.width;
    this.scratch[1] = 0;
    this.scratch[2] = 0;
    this.scratch[3] = 0;
    this.scratch[4] = this.color[0];
    this.scratch[5] = this.color[1];
    this.scratch[6] = this.color[2];
    this.scratch[7] = this.color[3];

    device.queue.writeBuffer(
      this.outline_buffer,
      0,
      this.scratch,
      0,
      this.scratch.length
    );
  }
}

// Fixed-function pipeline state shared by both outline pipelines:
//   - cullMode "front": inverted-hull technique - the shell's near-camera
//     (front, CCW-wound per glTF/WebGPU convention) faces are culled so
//     only the back faces of the extruded shell remain, forming the
//     silhouette rim. Validated against this repo's tracked static
//     (clone_tube.glb) and skinned (panzer.glb) assets - both are >99.9%
//     consistently CCW-wound, so no configurable front-face override is
//     exposed (the approved design calls for adding one only if real
//     assets show a need; they don't).
//   - frontFace defaults to "ccw" (WebGPU default, never overridden
//     anywhere else in this engine either) - kept implicit/default here
//     to match that convention rather than redundantly specifying it.
//   - depthWriteEnabled false: the outline must not write depth (matches
//     this engine's existing convention for non-primary-geometry passes:
//     collision/graph/blobshadow pipelines are also depthWriteEnabled:
//     false), so it never occludes anything via its own depth buffer
//     contribution.
//   - depthCompare "less" against the depth already written by the base
//     mesh drawn earlier in the same pass: the base mesh must be drawn
//     first (see StaticOutlineRenderPipeline/SkinnedOutlineRenderPipeline
//     doc comments) so the outline's extruded shell is correctly occluded
//     by nearer scene geometry (including the base mesh's own nearer
//     surfaces), which is the "opt-in, occluded by nearer geometry"
//     requirement.
//   - opaque by default: no blend state configured (matches a flat,
//     non-transparent outline; alpha is still exposed via set_line_color
//     for future-proofing even though it is not blended against the
//     background in v1).
export const OUTLINE_PRIMITIVE_STATE: GPUPrimitiveState = {
  topology: "triangle-list",
  cullMode: "front",
};

export const OUTLINE_DEPTH_STENCIL_STATE: GPUDepthStencilState = {
  format: "depth24plus",
  depthWriteEnabled: false,
  depthCompare: "less",
};
